import React, { useState } from 'react';
import axios from 'axios';

export default function CommitForm({ token, owner }) {
  const [repo, setRepo] = useState('');
  const [count, setCount] = useState(200);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [createRepo, setCreateRepo] = useState(true);
  const [description, setDescription] = useState('Auto-generated repository with commit history');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const res = await axios.post(`${apiUrl}/api/commits`, { 
        token, 
        owner, 
        repo, 
        count: parseInt(count),
        createRepo,
        description: createRepo ? description : undefined
      });
      const repoUrl = `https://github.com/${res.data.owner}/${res.data.repo}`;
      setStatus({ 
        type: 'success', 
        message: `${res.data.message} View it at: ${repoUrl}`,
        repoUrl
      });
    } catch (err) {
      setStatus({ 
        type: 'error', 
        message: err.response?.data?.error || err.response?.data?.details || 'Error occurred' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.userInfo}>
        <span style={styles.userLabel}>Logged in as:</span>
        <span style={styles.username}>{owner}</span>
      </div>
      
      <div 
        style={styles.toggleGroup}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#565869';
          e.currentTarget.style.background = '#4a4b5c';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#565869';
          e.currentTarget.style.background = '#40414f';
        }}
      >
        <label style={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={createRepo}
            onChange={e => setCreateRepo(e.target.checked)}
            disabled={loading}
            style={styles.checkbox}
          />
          <span>Create new repository</span>
        </label>
        <small style={styles.toggleHint}>
          {createRepo 
            ? 'A new repository will be created automatically' 
            : 'Use an existing repository (must already exist)'}
        </small>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <span style={styles.labelIcon}>📦</span>
          Repository Name
        </label>
        <input 
          value={repo} 
          onChange={e => setRepo(e.target.value)} 
          placeholder={createRepo ? "my-awesome-repo" : "existing-repo-name"}
          required 
          style={styles.input}
          disabled={loading}
          onFocus={(e) => {
            e.target.style.borderColor = '#10a37f';
            e.target.style.background = '#4a4b5c';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#565869';
            e.target.style.background = '#40414f';
          }}
        />
        <small style={styles.hint}>
          {createRepo 
            ? 'Name for the new repository' 
            : 'Name of existing repository (not the full URL)'}
        </small>
      </div>

      {createRepo && (
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            <span style={styles.labelIcon}>📝</span>
            Repository Description (optional)
          </label>
          <input 
            value={description} 
            onChange={e => setDescription(e.target.value)} 
            placeholder="Auto-generated repository with commit history"
            style={styles.input}
            disabled={loading}
            onFocus={(e) => {
              e.target.style.borderColor = '#10a37f';
              e.target.style.background = '#4a4b5c';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#565869';
              e.target.style.background = '#40414f';
            }}
          />
        </div>
      )}
      
      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <span style={styles.labelIcon}>🔢</span>
          Number of Commits
        </label>
        <input 
          type="number" 
          min={1} 
          max={1000} 
          value={count} 
          onChange={e => setCount(e.target.value)} 
          required 
          style={styles.input}
          disabled={loading}
          onFocus={(e) => {
            e.target.style.borderColor = '#10a37f';
            e.target.style.background = '#4a4b5c';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#565869';
            e.target.style.background = '#40414f';
          }}
        />
        <small style={styles.hint}>
          Enter the number of commits you want (1-1000). Optimized for maximum speed.
        </small>
      </div>
      
      <button 
        type="submit" 
        style={{
          ...styles.submitButton,
          ...(loading ? styles.submitButtonDisabled : {}),
        }}
        disabled={loading}
        onMouseEnter={(e) => {
          if (!loading) {
            e.target.style.background = '#0d8f6e';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(16, 163, 127, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.target.style.background = '#10a37f';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(16, 163, 127, 0.3)';
          }
        }}
      >
        {loading ? (
          <>
            <span style={styles.spinner}>⏳</span>
            Generating Commits...
          </>
        ) : (
          <>
            <span>✨</span>
            Generate Commits
          </>
        )}
      </button>
      
      {status && (
        <div style={{
          ...styles.status,
          ...(status.type === 'success' ? styles.statusSuccess : styles.statusError)
        }}>
          {status.type === 'success' ? '✅' : '❌'} {status.message}
          {status.repoUrl && (
            <div style={{ marginTop: '8px' }}>
              <a 
                href={status.repoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={styles.repoLink}
                onMouseEnter={(e) => {
                  e.target.style.background = '#4a4b5c';
                  e.target.style.borderColor = '#10a37f';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#40414f';
                  e.target.style.borderColor = '#565869';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Open Repository →
              </a>
            </div>
          )}
        </div>
      )}
    </form>
  );
}

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    animation: 'fadeIn 0.5s ease-out',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: '#40414f',
    borderRadius: '10px',
    marginBottom: '8px',
    border: '1px solid #565869',
    animation: 'slideIn 0.4s ease-out',
  },
  userLabel: {
    fontSize: '13px',
    color: '#8e8ea0',
  },
  username: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#ececf1',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#ececf1',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    letterSpacing: '0.2px',
  },
  labelIcon: {
    fontSize: '16px',
  },
  input: {
    padding: '12px 16px',
    fontSize: '15px',
    background: '#40414f',
    border: '1px solid #565869',
    borderRadius: '10px',
    outline: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#ececf1',
    fontFamily: 'inherit',
  },
  hint: {
    fontSize: '12px',
    color: '#8e8ea0',
    marginTop: '-4px',
  },
  submitButton: {
    background: '#10a37f',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 12px rgba(16, 163, 127, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '8px',
    letterSpacing: '0.2px',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none !important',
  },
  spinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
    fontSize: '16px',
  },
  status: {
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    animation: 'slideIn 0.3s ease-out',
    border: '1px solid',
  },
  statusSuccess: {
    background: '#1a3a2e',
    color: '#10a37f',
    borderColor: '#10a37f',
  },
  statusError: {
    background: '#5c2d2d',
    color: '#fca5a5',
    borderColor: '#7f1d1d',
  },
  toggleGroup: {
    padding: '16px',
    background: '#40414f',
    borderRadius: '10px',
    border: '1px solid #565869',
    transition: 'all 0.2s ease',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ececf1',
    cursor: 'pointer',
    userSelect: 'none',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#10a37f',
  },
  toggleHint: {
    display: 'block',
    fontSize: '12px',
    color: '#8e8ea0',
    marginTop: '8px',
  },
  repoLink: {
    color: '#10a37f',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '14px',
    display: 'inline-block',
    padding: '6px 12px',
    background: '#40414f',
    borderRadius: '8px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid #565869',
  },
};
